import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Drawer,
  IconButton,
  Button
} from '@mui/material';
import { 
  ArrowLeft, 
  Lightning,
  Rocket,
  Microphone,
  Files,
  Folders,
  FileText,
  Robot,
  RocketLaunch,
  Question
} from '@phosphor-icons/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocation, useNavigate } from 'react-router-dom';
import helpContent from '@/content/helpContent';
import { SupportSubmissionForm } from '@/components/SupportSubmissionForm/SupportSubmissionForm';
import { useHeaderContext } from '@/contexts/HeaderContext';
import { useHelpDrawer } from '@/contexts/HelpDrawerContext';

const HELP_SECTIONS = [
  { id: 'getting-started', title: 'Getting Started', heading: '## Getting Started', icon: Rocket },
  { id: 'recording-sessions', title: 'Recording Sessions', heading: '## Recording Sessions', icon: Microphone },
  { id: 'files', title: 'Files', heading: '## Files', icon: Files },
  { id: 'folders', title: 'Folders', heading: '## Folders', icon: Folders },
  { id: 'templates', title: 'Templates', heading: '## Templates', icon: FileText },
  { id: 'ai-features', title: 'AI Features', heading: '## AI Features', icon: Robot },
  { id: 'advanced-features', title: 'Advanced Features', heading: '## Advanced Features', icon: RocketLaunch },
  { id: 'support', title: 'Support', heading: '## Support', icon: Question }
];

import { DRAWER_CONFIG } from '@/constants';

const DRAWER_WIDTH = DRAWER_CONFIG.DEFAULT_WIDTH;

export const HelpPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const { isOpen: helpDrawerOpen, closeDrawer: closeHelpDrawer } = useHelpDrawer();

  // Set header text
  useEffect(() => {
    setHeaderText('Help & Documentation');
    setMenuActions(null);
    
    return () => {
      setHeaderText('');
      setMenuActions(null);
    };
  }, [setHeaderText, setMenuActions]);

  // Extract section from hash or default to first section
  const currentSection = useMemo(() => {
    const hash = location.hash.replace('#', '');
    return hash || HELP_SECTIONS[0].id;
  }, [location.hash]);

  // Extract content for current section
  const currentContent = useMemo(() => {
    if (!helpContent) return 'Content not available.';
    
    // Extract section content based on heading
    const section = HELP_SECTIONS.find(s => s.id === currentSection);
    if (!section) return helpContent;
    
    // Find the section in markdown
    const lines = helpContent.split('\n');
    let startIndex = -1;
    let endIndex = lines.length;
    
    // Find section start
    for (let i = 0; i < lines.length; i++) {
      if (lines[i] === section.heading || lines[i].startsWith(section.heading)) {
        startIndex = i;
        break;
      }
    }
    
    // Find next section or end
    if (startIndex !== -1) {
      for (let i = startIndex + 1; i < lines.length; i++) {
        if (lines[i].startsWith('## ')) {
          endIndex = i;
          break;
        }
      }
    }
    
    if (startIndex === -1) return helpContent;
    return lines.slice(startIndex, endIndex).join('\n');
  }, [currentSection]);

  const handleSectionClick = (sectionId: string) => {
    navigate(`/help#${sectionId}`, { replace: true });
    if (isMobile) {
      closeHelpDrawer();
    }
    // Scroll to top of content
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sidebarContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Back to App Button - Matches header height */}
      <Box
        onClick={() => navigate('/create/record')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          minHeight: isMobile ? '56px' : '64px',
          borderBottom: `1px solid ${theme.palette.divider}`,
          cursor: 'pointer',
          color: theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary
          }
        }}
      >
        <ArrowLeft size={16} />
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>
          Back to App
        </Typography>
      </Box>

      <Box sx={{ p: 2, pb: 1 }}>
      </Box>
      <Box sx={{ px: 2, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, overflow: 'auto', py: 1 }}>
        {HELP_SECTIONS.map((section) => {
          const isActive = currentSection === section.id;
          const IconComponent = section.icon;
          return (
            <Box
              key={section.id}
              onClick={() => handleSectionClick(section.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                px: 3,
                py: 1.5,
                borderRadius: '10px',
                color: isActive
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
                backgroundColor: isActive
                  ? theme.palette.action.selected
                  : 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                justifyContent: 'flex-start',
                pointerEvents: 'auto',
                position: 'relative',
                zIndex: 1,
                '&:hover': {
                  backgroundColor: isActive
                    ? theme.palette.action.selected
                    : theme.palette.action.hover,
                  color: isActive
                    ? theme.palette.primary.main
                    : theme.palette.text.primary
                }
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isActive ? 1 : 0.7,
                  flexShrink: 0
                }}
              >
                <IconComponent size={20} weight={isActive ? 'regular' : 'regular'} />
              </Box>
              <Box sx={{ fontSize: '14px', fontWeight: isActive ? 600 : 500, textAlign: 'left' }}>
                {section.title}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper
            }
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          anchor="left"
          open={helpDrawerOpen}
          onClose={closeHelpDrawer}
          keepMounted={false}
          ModalProps={{
            keepMounted: false,
            disableScrollLock: true,
            disableEnforceFocus: true,
            disableRestoreFocus: true,
            sx: {
              '& .MuiBackdrop-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: '85%',
              maxWidth: DRAWER_WIDTH,
              boxSizing: 'border-box',
              backgroundColor: theme.palette.background.paper
            }
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          backgroundColor: theme.palette.background.paper,
          position: 'relative'
        }}
      >
        {/* Content Area */}
        <Box
          sx={{
            maxWidth: 800,
            width: '100%',
            mx: 'auto', // Center the content
            pt: { xs: 1, sm: 2, md: 2 },
            px: { xs: 3, sm: 4, md: 5 },
            pb: { xs: 3, sm: 4, md: 5 },
            textAlign: 'left',
            '& h1': {
              fontSize: '1.25rem',
              fontWeight: 600,
              mb: 3,
              color: theme.palette.text.primary,
              lineHeight: 1.2,
              textAlign: 'left'
            },
            '& h2': {
              fontSize: '1.25rem',
              fontWeight: 600,
              mt: 2,
              mb: 2,
              color: theme.palette.text.primary,
              scrollMarginTop: '80px', // For anchor links
              paddingBottom: '8px',
              borderBottom: `2px solid ${theme.palette.divider}`,
              textAlign: 'left'
            },
            '& h3': {
              fontSize: '1.35rem',
              fontWeight: 600,
              mt: 4,
              mb: 2,
              color: theme.palette.text.primary,
              textAlign: 'left'
            },
            '& h4': {
              fontSize: '1.1rem',
              fontWeight: 600,
              mt: 2,
              mb: 1,
              color: theme.palette.text.primary
            },
            '& p': {
              fontSize: '1rem',
              lineHeight: 1.8,
              mb: 2.5,
              color: theme.palette.text.secondary
            },
            '& ul, & ol': {
              mb: 3,
              pl: 3,
              '& li': {
                mb: 1.5,
                lineHeight: 1.8,
                color: theme.palette.text.secondary,
                '&::marker': {
                  color: theme.palette.primary.main
                }
              }
            },
            '& strong': {
              fontWeight: 600,
              color: theme.palette.text.primary
            },
            '& code': {
              backgroundColor: theme.palette.action.hover,
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.9em',
              fontFamily: 'monospace'
            },
            '& pre': {
              backgroundColor: theme.palette.action.hover,
              padding: '16px',
              borderRadius: '8px',
              overflow: 'auto',
              mb: 2,
              '& code': {
                backgroundColor: 'transparent',
                padding: 0
              }
            },
            '& hr': {
              border: 'none',
              borderTop: `2px solid ${theme.palette.divider}`,
              my: 5,
              mx: 0
            },
            '& a': {
              color: theme.palette.primary.main,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            },
            '& blockquote': {
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              pl: 2,
              ml: 0,
              fontStyle: 'italic',
              color: theme.palette.text.secondary
            }
          }}
        >
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ node, ...props }) => {
                // Inject AI chat block right after "Getting Started" heading
                const childrenArray = Array.isArray(props.children) ? props.children : [props.children];
                const firstChild = childrenArray[0];
                const headingText = typeof firstChild === 'string' ? firstChild : 
                                  (typeof firstChild === 'object' && firstChild && 'props' in firstChild && 
                                   typeof firstChild.props?.children === 'string' ? firstChild.props.children : '');
                
                if (currentSection === 'getting-started' && headingText.includes('Getting Started')) {
                  return (
                    <>
                      <h2 {...props} />
                      <Box
                        component="div"
                        sx={{
                          mt: 2,
                          mb: 3,
                          p: 3,
                          backgroundColor: theme.palette.background.default,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`
                        }}
                      >
                        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                          Need help getting started?
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                          Ask our AI assistant anything about IntakeAI. It can help you understand features, troubleshoot issues, and guide you through workflows.
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={() => navigate('/ai-chat')}
                          startIcon={<Lightning size={18} weight="fill" />}
                          sx={{
                            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
                            color: '#fff',
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
                            }
                          }}
                        >
                          Ask AI Assistant
                        </Button>
                      </Box>
                    </>
                  );
                }
                return <h2 {...props} />;
              }
            }}
          >
            {currentContent}
          </ReactMarkdown>
          
          {/* Show support form only in Support section */}
          {currentSection === 'support' && (
            <SupportSubmissionForm />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default HelpPage;

